// frontend/src/services/pushClient.js
// Cliente Web Push: registro de Service Worker + suscripción VAPID.
// La clave pública se obtiene del backend — nunca se hardcodea.
import { api } from './api';

const SW_URL = '/sw.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// Convierte un ArrayBuffer/Uint8Array a base64url (sin padding) para comparar
// la clave del servidor de aplicaciones de una suscripción existente.
function arrayBufferToBase64Url(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Normaliza una clave VAPID a base64url sin padding para comparaciones estables.
function normalizeKey(key) {
  return (key || '').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL);
    return reg;
  } catch (e) {
    console.warn('SW registro falló:', e);
    return null;
  }
}

/**
 * Garantiza una suscripción push activa para el usuario autenticado.
 * Pide permiso solo si aún no se decidió. Devuelve true si quedó suscrito.
 */
export async function ensurePushSubscription() {
  if (!pushSupported()) return false;
  try {
    const keyRes = await api.chat.pushPublicKey();
    if (!keyRes.data?.enabled || !keyRes.data?.public_key) return false;

    if (Notification.permission === 'denied') return false;
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
    }

    const reg = (await navigator.serviceWorker.getRegistration(SW_URL)) || (await registerServiceWorker());
    if (!reg) return false;
    await navigator.serviceWorker.ready;

    const serverKey = normalizeKey(keyRes.data.public_key);

    let sub = await reg.pushManager.getSubscription();
    // Si ya hay suscripción pero su applicationServerKey NO coincide con la
    // clave VAPID actual del backend (rotación de claves o suscripción de otro
    // entorno), hay que desuscribir y volver a crearla; de lo contrario el
    // backend nunca podrá entregar los push y los dará de baja por 410.
    if (sub) {
      const subKey = arrayBufferToBase64Url(sub.options?.applicationServerKey);
      if (subKey && subKey !== serverKey) {
        try { await sub.unsubscribe(); } catch (_) { /* noop */ }
        sub = null;
      }
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.public_key),
      });
    }
    await api.chat.pushSubscribe(sub.toJSON());
    return true;
  } catch (e) {
    console.warn('Push subscribe falló:', e);
    return false;
  }
}

/**
 * Estado actual del permiso de notificaciones de forma normalizada.
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export function pushPermissionState() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

/**
 * Re-solicita explícitamente el permiso de notificaciones (gesto del usuario) y,
 * si queda 'granted', crea/asegura la suscripción push (POST /push/subscribe).
 * Necesario porque ensurePushSubscription() retorna en silencio si el permiso ya
 * está en 'denied'/'default' y nunca aparecía UI para pedirlo.
 * @returns {Promise<'granted'|'denied'|'default'|'unsupported'>} estado final del permiso
 */
export async function requestPushPermissionExplicitly() {
  if (!pushSupported()) return 'unsupported';
  try {
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      await ensurePushSubscription();
    }
    return perm;
  } catch (e) {
    console.warn('requestPushPermission falló:', e);
    return pushPermissionState();
  }
}

// Evita registrar el listener de mensajes del SW más de una vez.
let _navListenerBound = false;

/**
 * Conecta los mensajes 'push-navigate' que envía el Service Worker (al hacer
 * click en una notificación) con la navegación SPA. Esto es necesario porque
 * en algunos navegadores el SW no puede usar client.navigate() y recurre a
 * postMessage; sin este listener los deep links (abrir canal, contestar /
 * rechazar llamada) se perderían silenciosamente.
 *
 * @param {(url: string) => void} navigate  callback (p.ej. react-router navigate)
 * @returns {() => void} función para desmontar el listener
 */
export function initPushNavigation(navigate) {
  if (!('serviceWorker' in navigator) || typeof navigate !== 'function') return () => {};
  const handler = (event) => {
    const d = event.data || {};
    if (d.type === 'push-navigate' && typeof d.url === 'string') {
      // Pasamos la URL relativa a react-router para no recargar la página y que
      // el manejo de deep link (?channel/?call/?action) se procese vía routing.
      try {
        const u = new URL(d.url, window.location.origin);
        navigate(u.pathname + u.search);
      } catch (_) {
        navigate(d.url);
      }
    }
  };
  // Permitir re-registro si se montó de nuevo (HMR / remount) pero sin duplicar.
  navigator.serviceWorker.removeEventListener('message', handler);
  navigator.serviceWorker.addEventListener('message', handler);
  _navListenerBound = true;
  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
    _navListenerBound = false;
  };
}

export function isPushNavigationBound() {
  return _navListenerBound;
}

/** Desuscribe este navegador (llamar al hacer logout). */
export async function removePushSubscription() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      try { await api.chat.pushUnsubscribe(sub.endpoint); } catch (_) { /* noop */ }
      await sub.unsubscribe();
    }
  } catch (e) {
    console.warn('Push unsubscribe falló:', e);
  }
}
